import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useLoginUser, useIsAdmin, useIsGroupNurse } from "@/hooks/useAuth";
import {
  fetchPersonalRosterMonths,
  fetchWardRosterMonths,
  fetchRosterViewWards,
  rosterDateFromSearch,
  rosterViewHref,
  type RosterViewDate,
} from "@/services/roster/viewNavigation";

export function useRosterViewNavigation() {
  const user = useLoginUser(),
    isAdmin = useIsAdmin(),
    isGroupNurse = useIsGroupNurse();
  const location = useLocation(),
    navigate = useNavigate();
  const scope: "my" | "all" = location.pathname.endsWith("/all") ? "all" : "my";
  const accountScope = `${user?.account_id ?? ""}:${user?.office_id ?? ""}`;
  const personalMonths = useQuery({
    queryKey: ["roster-personal-months", accountScope],
    queryFn: ({ signal }) => fetchPersonalRosterMonths(signal),
    enabled: !!user,
  });
  const candidates = useQuery({
    queryKey: ["roster-view-wards", accountScope, isAdmin, isGroupNurse],
    queryFn: ({ signal }) => fetchRosterViewWards(isAdmin, signal),
    enabled: !!user && (isAdmin || isGroupNurse),
  });
  const homeId =
    personalMonths.data?.home_group_id ||
    user?.original_group_id ||
    user?.group_id ||
    "";
  const homeName =
    personalMonths.data?.home_group_name || user?.mb_part_name || homeId;
  const requestedWard = new URLSearchParams(location.search)
    .get("group_id")
    ?.trim();
  const [remembered, setRemembered] = useState<{
    account: string;
    my: RosterViewDate | null;
    all: RosterViewDate | null;
    ward: string;
  }>({ account: accountScope, my: null, all: null, ward: "" });
  const selectionIntent = useRef(remembered);
  const ownState =
    remembered.account === accountScope
      ? remembered
      : { account: accountScope, my: null, all: null, ward: "" };
  const groupId =
    scope === "all"
      ? requestedWard || ownState.ward || homeId
      : ownState.ward || homeId;
  const wardOptions = [
    { group_id: homeId, group_name: homeName },
    ...((isAdmin || isGroupNurse ? candidates.data : undefined) ?? [])
      .filter(
        (ward) =>
          ward.group_id !== homeId &&
          (!ward.office_id || ward.office_id === user?.office_id)
      )
      .sort((a, b) =>
        a.group_name.localeCompare(b.group_name, "ko-KR", { numeric: true })
      ),
  ].filter((ward) => !!ward.group_id);
  const wardAllowed = wardOptions.some((ward) => ward.group_id === groupId);
  const wardMonths = useQuery({
    queryKey: ["roster-view-months", accountScope, groupId],
    queryFn: ({ signal }) => fetchWardRosterMonths(groupId, signal),
    enabled: !!user && !!groupId && wardAllowed && scope === "all",
  });
  const homeIssuedMonths = useQuery({
    queryKey: ["roster-view-months", accountScope, homeId],
    queryFn: ({ signal }) => fetchWardRosterMonths(homeId, signal),
    enabled: !!user && !!homeId && scope === "my",
  });
  const personalList = (personalMonths.data?.months ?? []).map((date) => ({
    ...date,
    schedule_id: "",
  }));
  const allList = wardMonths.data ?? [];
  const activeList = scope === "my" ? personalList : allList;
  const now = new Date();
  const fallback = {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    schedule_id: "",
  };
  const requestedDate = rosterDateFromSearch(location.search);
  const rememberedDate = ownState[scope];
  const baseDate = requestedDate ?? rememberedDate ?? activeList[0] ?? fallback;
  const date =
    activeList.find(
      (item) => item.year === baseDate.year && item.month === baseDate.month
    ) ?? baseDate;
  const hasExplicitDate = requestedDate !== null;
  const hasRememberedDate = rememberedDate !== null;
  const activeListLength = activeList.length;
  const { year, month, schedule_id } = date;
  useLayoutEffect(() => {
    if (!hasExplicitDate && !hasRememberedDate && activeListLength === 0)
      return;
    setRemembered((prev) => {
      const base =
        prev.account === accountScope
          ? prev
          : { account: accountScope, my: null, all: null, ward: "" };
      const old = base[scope];
      if (
        old?.year === year &&
        old.month === month &&
        (scope === "my" || base.ward === groupId)
      )
        return prev;
      const next = {
        ...base,
        [scope]: { year, month, schedule_id },
        ward: scope === "all" ? groupId : base.ward,
      };
      selectionIntent.current = next;
      return next;
    });
  }, [
    accountScope,
    scope,
    year,
    month,
    schedule_id,
    groupId,
    hasExplicitDate,
    hasRememberedDate,
    activeListLength,
  ]);
  const selectDate = useCallback(
    (next: RosterViewDate) => {
      const current = selectionIntent.current;
      const previous =
        current.account === accountScope
          ? current
          : { account: accountScope, my: null, all: null, ward: "" };
      const ward = previous.ward || groupId;
      const state = { ...previous, account: accountScope, [scope]: next, ward };
      selectionIntent.current = state;
      setRemembered(state);
      navigate(rosterViewHref(scope, next, ward), { replace: true });
    },
    [navigate, scope, groupId, accountScope]
  );
  const selectWard = useCallback(
    (ward: string) => {
      const current = selectionIntent.current;
      const previous =
        current.account === accountScope
          ? current
          : { account: accountScope, my: null, all: null, ward: "" };
      const nextDate = previous.all ?? { year, month, schedule_id };
      const state = { ...previous, account: accountScope, all: nextDate, ward };
      selectionIntent.current = state;
      setRemembered(state);
      navigate(rosterViewHref("all", nextDate, ward), { replace: true });
    },
    [navigate, year, month, schedule_id, accountScope]
  );
  return {
    user,
    scope,
    date,
    groupId,
    homeId,
    wardOptions,
    wardAllowed,
    personalMonths,
    candidates,
    wardMonths,
    issuedList: activeList,
    personalScheduleId:
      homeIssuedMonths.data?.find(
        (item) => item.year === date.year && item.month === date.month
      )?.schedule_id ?? "",
    wardLoading: (isAdmin || isGroupNurse) && candidates.isPending,
    selectDate,
    selectWard,
    myHref: rosterViewHref(
      "my",
      ownState.my ?? personalList[0] ?? fallback,
      ""
    ),
    allHref: rosterViewHref(
      "all",
      ownState.all ?? date,
      ownState.ward || homeId
    ),
  };
}
